import React from "react";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { changeShowForm } from "../../../store/reducers/thunk";
import { Link, useNavigate } from "react-router-dom";
import lineFooter from "../../../assets/footerLine.png";
import PD_G_logo from "../../../assets/PD_G_logo.svg";
import LinkedInIcon from "../../../assets/LINKDIN.svg";
import InstagramIcon from "../../../assets/instagram.svg";
import YouTubeIcon from "../../../assets/youtube.svg";

interface AboutUsFooterProps {
  scrollToSection?: (sectionId: string) => void;
}

function AboutUsFooter({ scrollToSection }: AboutUsFooterProps) {
  const dispatch = useDispatch();

  function showFrom() {
    dispatch(changeShowForm(true));
  }

  const navigate = useNavigate();

  const socialLinks = [
    {
      name: "LINKEDIN",
      url: "https://www.linkedin.com/company/grupopenadefillo/",
    },
    { name: "INSTAGRAM", url: "https://www.instagram.com/pena.defillo/" },
    { name: "YOUTUBE", url: "https://www.youtube.com/@grupopenadefillo" },
  ];

  const navigationLinks = [
    { name: "PORTAFOLIO", to: "/home/portafolio" },
    { name: "EMPLEOS", to: "/careers" },
    {
      name: "SOBRE NOSOTROS",
      to: "/about",
      onClick: () => scrollToSection?.("title"),
    },
    { name: "CONTÁCTANOS", to: "/contact", onClick: showFrom },
    { name: "UBICACIÓN", to: "https://goo.gl/maps/sHnehVLCdqHvbGyH8" },
  ];

  return (
    <footer className="w-full h-screen bg-black flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-7xl flex flex-col justify-center gap-8 lg:gap-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: "backOut" }}
          className="text-center"
        >
          <motion.h2
            initial={{ opacity: 0, letterSpacing: "0.5rem" }}
            animate={{ opacity: 1, letterSpacing: "0.1rem" }}
            transition={{ duration: 1.2, delay: 0.5 }}
            className="text-white text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold font-montserrat mb-8"
          >
            <motion.span
              initial={{ opacity: 0, rotateY: -90 }}
              animate={{ opacity: 1, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="inline-block"
            >
              Únete{" "}
            </motion.span>
            <motion.span
              initial={{ opacity: 0, rotateY: 90 }}
              animate={{ opacity: 1, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="block lg:inline"
            >
              a{" "}
            </motion.span>
            <motion.span
              initial={{ opacity: 0, scale: 0.5, rotateX: -90 }}
              animate={{ opacity: 1, scale: 1, rotateX: 0 }}
              transition={{ duration: 1, delay: 1.2, ease: "backOut" }}
              className="inline-block"
            >
              este equipo
            </motion.span>
          </motion.h2>
          <motion.button
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.5, ease: "backOut" }}
            className="w-48 h-10 bg-white text-black border border-solid border-black font-montserrat font-medium text-sm leading-5 uppercase tracking-wider transition-colors duration-200 cursor-pointer hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            onClick={() => {
              navigate("/careers", { replace: true });
            }}
          >
            Ver empleos
          </motion.button>
        </motion.div>

        {/* Decorative Line */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="w-full"
        >
          <img
            draggable={false}
            src={lineFooter}
            className="w-full h-auto"
            alt="Decorative line"
          />
        </motion.div>

        {/* Footer Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center lg:text-left"
          >
            <h3 className="text-white text-xl lg:text-2xl font-bold font-montserrat mb-6">
              ¡SÍGUENOS!
            </h3>
            <div className="flex flex-col gap-4">
              {socialLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white text-lg font-medium font-montserrat hover:text-gray-300 transition-colors duration-300"
                >
                  {link.name}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Navigation Links */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center lg:text-right"
          >
            <img
              draggable={false}
              src={PD_G_logo}
              alt="Peña Defilló Logo"
              className="w-32 lg:w-40 h-auto mx-auto lg:ml-auto lg:mr-0 mb-6"
            />
            <div className="flex flex-col gap-4">
              {navigationLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                >
                  <Link
                    to={link.to}
                    onClick={link.onClick}
                    className="text-white text-lg font-medium font-montserrat hover:text-gray-300 transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="border-t border-gray-600 pt-8 text-center"
        >
          {/* Social Icons */}
          <div className="flex justify-center gap-6 mb-6">
            <a
              href="https://www.linkedin.com/company/grupopenadefillo/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                draggable={false}
                src={LinkedInIcon}
                alt="LinkedIn"
                className="w-8 h-8 hover:opacity-70 transition-opacity duration-300"
              />
            </a>
            <a
              href="https://www.instagram.com/pena.defillo/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                draggable={false}
                src={InstagramIcon}
                alt="Instagram"
                className="w-8 h-8 hover:opacity-70 transition-opacity duration-300"
              />
            </a>
            <a
              href="https://www.youtube.com/@grupopenadefillo"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                draggable={false}
                src={YouTubeIcon}
                alt="YouTube"
                className="w-8 h-8 hover:opacity-70 transition-opacity duration-300"
              />
            </a>
          </div>

          {/* Copyright */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-white text-sm font-montserrat">
            <p>&copy; Peña Defilló {new Date().getFullYear()}</p>
            <span className="hidden sm:inline">•</span>
            <a
              href="https://sharepenadefillo-my.sharepoint.com/personal/rrhh_sharepenadefillo_onmicrosoft_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Frrhh%5Fsharepenadefillo%5Fonmicrosoft%5Fcom%2FDocuments%2FPol%C3%ADtica%20de%20privacidad%20de%20Grupo%20Pe%C3%B1a%20Defill%C3%B3%2Epdf&parent=%2Fpersonal%2Frrhh%5Fsharepenadefillo%5Fonmicrosoft%5Fcom%2FDocuments&ga=1"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-300 transition-colors duration-300"
            >
              Política de Privacidad
            </a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}

export default AboutUsFooter;
